import {
  GetMeUpcomingReservationsV1Params,
  GetBookableServicesV1Params,
  AppSettingsContentResponseModel,
  IApiContentResponseModel,
  ReservationDetailDto,
  ReservationShareDto,
  ReservationSummaryDto,
  ServiceCategoriesContentResponseModel,
  ServiceCategoriesContentResponseModelContentType,
  SuggestionContentResponseModel,
  SuggestionContentResponseModelContentType,
  VenueServiceDto,
} from '@/services/platform/api/model';
import {
  getMeReservationByIdV1,
  getMeUpcomingReservationsV1,
  cancelMeReservationV1,
  shareMeReservationV1,
  getMeReservationByShareCodeV1,
} from '../../api/me-—-profile/me-—-profile';
import { getBookableServicesV1 } from '../../api/reservations/reservations';
import { getContents, getContentById } from '../cms';

/**
 * Fetches the authenticated user's upcoming reservations.
 *
 * @param request - Query params: `take` (optional) — maximum number of reservations to return.
 * @param locale - BCP 47 locale tag (e.g. `"en-GB"`, `"pt-PT"`) forwarded as `Accept-Language`
 *   so the API returns CMS content in the correct language.
 * @returns Array of `ReservationSummaryDto` on HTTP 200, ordered by date ascending.
 * @returns `undefined` on non-2xx response or network error.
 */
export async function getUpcomingReservations(
  request: GetMeUpcomingReservationsV1Params,
  locale: string
): Promise<ReservationSummaryDto[] | undefined> {
  try {
    const response = await getMeUpcomingReservationsV1(request, {
      headers: locale
        ? {
            'Accept-Language': locale,
          }
        : {},
    });

    if (response.status === 200) {
      return response?.data as ReservationSummaryDto[];
    } else {
      console.warn(`getMeUpcomingReservations: unexpected status ${response.status}`, response.data);
      return undefined;
    }
  } catch (error) {
    console.warn('getMeUpcomingReservations: request failed:', error);
    return undefined;
  }
}

/**
 * Fetches a single reservation by ID, enriched with venue and service-category content from the CMS.
 *
 * @param id - The reservation's public GUID.
 * @param locale - BCP 47 locale tag (e.g. `"en-GB"`, `"pt-PT"`) forwarded as `Accept-Language`
 *   so venue and service-category content is returned in the correct language.
 * @returns `ReservationDetailDto` on HTTP 200.
 * @returns `undefined` on 401 (unauthenticated), 404 (not found or not owned by the current user),
 *   or network error.
 */
export async function getReservation(id: string, locale: string): Promise<ReservationDetailDto | undefined> {
  try {
    const response = await getMeReservationByIdV1(id, {
      headers: locale
        ? {
            'Accept-Language': locale,
          }
        : {},
    });
    const { status, data } = response as { status: number; data: unknown };

    if (status === 200) {
      return data as ReservationDetailDto;
    }

    console.warn(`getReservation: unexpected status ${status} for id "${id}"`);
    return undefined;
  } catch (error) {
    console.warn(`getReservation: request failed for id "${id}":`, error);
    return undefined;
  }
}

/**
 * Generates (or returns the existing) share code for a reservation.
 *
 * The code can be used to access the reservation via the public share link
 * without authentication. Calling this endpoint multiple times for the same
 * reservation returns the same code rather than generating a new one.
 *
 * @param id - The reservation's public GUID.
 * @returns `ReservationShareDto` containing the share `code` on HTTP 200.
 * @returns `undefined` on 401 (unauthenticated), 404 (not found or not owned
 *   by the current user), or network error.
 */
export async function getShareCode(id: string): Promise<ReservationShareDto | undefined> {
  try {
    const response = await shareMeReservationV1(id);

    if (response.status === 200) {
      return response.data as ReservationShareDto;
    }

    console.warn(`getShareCode: unexpected status ${response.status} for id "${id}"`);
    return undefined;
  } catch (error) {
    console.warn(`getShareCode: request failed for id "${id}":`, error);
    return undefined;
  }
}

/**
 * Fetches a reservation by its public GUID and share code, without requiring authentication.
 *
 * The share code acts as the sole authorisation token — no ownership validation is performed.
 * Use this to load a reservation on behalf of a guest who received a share link.
 *
 * @param id - The reservation's public GUID.
 * @param code - The share code previously generated via `getShareCode`.
 * @param locale - BCP 47 locale tag (e.g. `"en-GB"`, `"pt-PT"`) forwarded as `Accept-Language`
 *   so venue and service-category content is returned in the correct language.
 * @returns `ReservationDetailDto` on HTTP 200.
 * @returns `undefined` on 401 (invalid or missing share code), 404 (reservation not found),
 *   or network error.
 */
export async function getReservationShared(
  id: string,
  code: string,
  locale: string
): Promise<ReservationDetailDto | undefined> {
  try {
    const response = await getMeReservationByShareCodeV1(id, code, {
      headers: locale ? { 'Accept-Language': locale } : {},
    });
    const { status, data } = response as { status: number; data: unknown };

    if (status === 200) {
      return data as ReservationDetailDto;
    }

    console.warn(`getReservationShared: unexpected status ${status} for id "${id}"`);
    return undefined;
  } catch (error) {
    console.warn(`getReservationShared: request failed for id "${id}":`, error);
    return undefined;
  }
}

/**
 * Cancels a reservation by ID.
 *
 * @param id - The reservation's public GUID.
 * @returns `true` on HTTP 200 or 204 (cancelled successfully).
 * @returns `false` on non-2xx response or network error.
 */
export async function cancelReservation(id: string): Promise<boolean> {
  try {
    const response = await cancelMeReservationV1(id);
    return response.status === 204;
  } catch (error) {
    console.warn(`cancelReservation: request failed for id "${id}":`, error);
    return false;
  }
}

/**
 * Resolves the Book page's own `serviceCategories` picker (MultiNodeTreePicker) into full
 * ServiceCategories content. Picker stubs only carry `id` (`properties: {}`), so each is
 * resolved individually via `getContentById`, mirroring `getSuggestions`.
 *
 * @param stubs - Picker stub objects, each with an optional `id` field
 *   (typically `content.properties.serviceCategories ?? []`).
 * @param locale - BCP 47 locale tag (e.g. `"en-GB"`, `"pt-PT"`) forwarded as `Accept-Language`
 *   so category names are returned in the correct language.
 * @returns Array of fully populated `ServiceCategoriesContentResponseModel` items.
 * @returns `[]` when all fetches fail or no stubs are provided.
 */
export async function getServiceCategories(
  stubs: { id?: string }[],
  locale: string
): Promise<ServiceCategoriesContentResponseModel[]> {
  const ids = stubs.map(s => s.id).filter((id): id is string => !!id);

  const results = await Promise.all(ids.map(id => getContentById(id, locale)));

  return results.filter(
    (item): item is ServiceCategoriesContentResponseModel =>
      item?.contentType === ServiceCategoriesContentResponseModelContentType.serviceCategories
  );
}

/**
 * Resolves a list of CMS suggestion picker stubs to their full content models.
 *
 * Picker stubs returned by the CMS carry only `id` — `properties` is empty `{}`.
 * This function fetches each stub's full content in parallel via `getContentById`
 * so the caller receives completely populated `SuggestionContentResponseModel` items.
 * Stubs without an `id` and items whose `contentType` is not `"suggestion"` are
 * silently discarded.
 *
 * @param stubs - Picker stub objects, each with an optional `id` field
 *   (typically `properties.suggestionsPicker ?? []`).
 * @param locale - BCP 47 locale tag (e.g. `"en-GB"`, `"pt-PT"`) forwarded as
 *   `Accept-Language` so content is returned in the correct language.
 * @returns Array of fully populated `SuggestionContentResponseModel` items,
 *   preserving the order of the input stubs.
 * @returns `[]` when all fetches fail or no stubs are provided.
 */
export async function getSuggestions(
  stubs: { id?: string }[],
  locale: string
): Promise<SuggestionContentResponseModel[]> {
  const ids = stubs.map(s => s.id).filter((id): id is string => !!id);

  const results = await Promise.all(ids.map(id => getContentById(id, locale)));

  return results.filter(
    (item): item is SuggestionContentResponseModel =>
      item?.contentType === SuggestionContentResponseModelContentType?.suggestion
  );
}

/**
 * Finds the booking flow node assigned to a specific service category.
 *
 * The booking intro node ID is read from `settings.properties.bookingIntro.id`.
 * If the settings object is absent or the ID is missing, the function returns
 * `undefined` immediately without making a network call.
 *
 * Searches the descendants of the booking intro content tree for a node whose
 * `serviceCategory` property matches `categoryId`. The first match is used
 * because each category maps to exactly one flow entry point.
 *
 * `expand: 'properties[$all]'` is included so the returned node carries full
 * property data, making the route path available without a second fetch.
 *
 * @param settings - App settings content model whose `properties.bookingIntro.id`
 *   identifies the root of the booking content tree to search.
 * @param categoryId - CMS node ID of the selected service category, used as
 *   the filter value for `property:serviceCategory`.
 * @param locale - BCP 47 locale tag (e.g. `"en-GB"`, `"pt-PT"`) forwarded as
 *   `Accept-Language`.
 * @returns The matching flow `IApiContentResponseModel` (with full properties) on success.
 * @returns `undefined` when settings are missing, no matching flow is found, or on network error.
 */
export async function getFlowByServiceCategoryId(
  settings: AppSettingsContentResponseModel | undefined,
  categoryId: string,
  locale: string
): Promise<IApiContentResponseModel | undefined> {
  const bookingIntroId = settings?.properties?.bookingIntro?.id;
  if (!bookingIntroId) return undefined;

  try {
    const result = await getContents(
      {
        skip: 0,
        take: 1,
        fetch: `descendants:${bookingIntroId}`,
        filter: [`property:serviceCategory:${categoryId}`],
        expand: 'properties[$all]',
      },
      locale
    );

    return result?.items?.[0] ?? undefined;
  } catch (error) {
    console.warn(`getFlowByServiceCategoryId: request failed for category "${categoryId}":`, error);
    return undefined;
  }
}

/**
 * Fetches published, active bookable services enriched with venue and
 * service-category data from the CMS.
 *
 * Results are culture-aware: pass `locale` to receive CMS content (names,
 * descriptions, images) in the correct language via `Accept-Language`.
 *
 * Both filter params are optional — omitting them returns all services
 * visible to the current member.
 *
 * @param params - Optional filters:
 *   - `serviceCategoryNodeKey` — limits results to a specific service category (O quê?).
 *   - `locationNodeKey` — limits results to a specific location (Onde?).
 * @param locale - BCP 47 locale tag (e.g. `"en-GB"`, `"pt-PT"`) forwarded as
 *   `Accept-Language`.
 * @returns Array of `VenueServiceDto` on HTTP 200, ordered as returned by the API.
 * @returns `undefined` on HTTP 500 or network error.
 */
export async function getBookableServices(
  params?: GetBookableServicesV1Params,
  locale?: string
): Promise<VenueServiceDto[] | undefined> {
  try {
    const response = await getBookableServicesV1(params, {
      headers: locale ? { 'Accept-Language': locale } : {},
    });

    if (response.status === 200) {
      return response.data as VenueServiceDto[];
    }

    console.warn(`getBookableServices: unexpected status ${response.status}`);
    return undefined;
  } catch (error) {
    console.warn('getBookableServices: request failed:', error);
    return undefined;
  }
}
